import { useState } from "react";
import { CaretDown, CaretUp, Lightbulb, MapPin, Heart, Microphone, BookOpen, Sparkle } from "@phosphor-icons/react";

/**
 * Informative-only tips card shown ABOVE the genre selector in CreateSong.
 * Does NOT affect the song generation flow.
 * Collapsible — user preference persists in localStorage.
 */
const TIPS = [
  {
    icon: MapPin,
    title: "Cuenta una historia REAL",
    body: "Usa nombres, lugares y fechas reales. Ej: «Mi mamá Lupita, Mazatlán, 1998». Entre más específico, más se conecta tu canción contigo.",
    color: "#FFB800",
  },
  {
    icon: Heart,
    title: "Elige el sentimiento correcto",
    body: "El mood (fiestero, romántico, melancólico…) define el tono. Una historia bonita con mood equivocado se siente forzada.",
    color: "#FF4B00",
  },
  {
    icon: Microphone,
    title: "Menciona artistas que te gustan",
    body: "En «Estilo de referencia» puedes poner: Peso Pluma, Banda MS, Vicente Fernández. La IA imitará su tono lírico y vocabulario.",
    color: "#B4F34A",
  },
  {
    icon: BookOpen,
    title: "Historia ideal: 100-300 palabras",
    body: "Demasiado corto → letra genérica. Demasiado largo → la IA se pierde. Apunta a 2-3 párrafos jugosos con anécdotas concretas.",
    color: "#4BC8DC",
  },
  {
    icon: Sparkle,
    title: "Detalles que enamoran",
    body: "Incluye dichos familiares, olores, comidas, fechas importantes, frases que repite alguien. Eso es lo que la hace única.",
    color: "#FF9CC8",
  },
];

export const CreateTips = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("tm_tips_collapsed") === "1"; }
    catch { return false; }
  });

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem("tm_tips_collapsed", next ? "1" : "0"); } catch { /* ignore */ }
  };

  return (
    <div className="mb-8 rounded-3xl bg-gradient-to-br from-[#FFB800]/8 via-[#0d0d0d] to-[#FF4B00]/8 border border-[#FFB800]/25 overflow-hidden" data-testid="create-tips-card">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors"
        data-testid="create-tips-toggle"
      >
        <div className="w-10 h-10 rounded-full bg-[#FFB800]/15 flex items-center justify-center shrink-0">
          <Lightbulb size={22} weight="fill" className="text-[#FFB800]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-base leading-tight">5 tips para que tu canción salga perfecta</p>
          <p className="text-xs text-white/55 mt-0.5">
            {collapsed ? "Tócame para verlos antes de empezar" : "Léelos antes de elegir tu género — son 30 segundos"}
          </p>
        </div>
        <div className="shrink-0 text-white/50">
          {collapsed ? <CaretDown size={18} weight="bold" /> : <CaretUp size={18} weight="bold" />}
        </div>
      </button>

      {!collapsed && (
        <div className="px-4 pb-4 pt-1" data-testid="create-tips-list">
          <div className="rounded-2xl bg-black/30 border border-white/5 overflow-hidden">
            {TIPS.map((tip, idx) => {
              const Icon = tip.icon;
              const last = idx === TIPS.length - 1;
              return (
                <div
                  key={tip.title}
                  className={`flex items-start gap-3 px-4 py-3 ${last ? "" : "border-b border-white/5"}`}
                  data-testid={`create-tip-${idx + 1}`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: `${tip.color}1a`, color: tip.color }}
                  >
                    <Icon size={16} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm leading-snug">{tip.title}</p>
                    <p className="text-xs text-white/60 mt-1 leading-relaxed">{tip.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-white/40 mt-3 text-center px-2">
            💡 Estos consejos son sólo recomendaciones. La IA hará un excelente trabajo aunque escribas menos — pero entre mejor sea tu historia, más se va a parecer a ti.
          </p>
        </div>
      )}
    </div>
  );
};
